import { ref, Ref, shallowRef } from "vue";

export enum RequestState {
  None = "none",
  Loading = "loading",
  Failed = "failed",
  Ready = "ready"
}

export class RequestError extends Error {
  public constructor(private _errorCode: string, private _errorMessage: string) {
    super(_errorMessage);
  }

  public get errorCode(): string {
    return this._errorCode;
  }

  public get errorMessage(): string {
    return this._errorMessage;
  }
}

export type IServiceRefs<T> = {
  state: Ref<RequestState>;
  value: Ref<T>;
  error: Ref<RequestError | null>;
}

type IDefaultValues<T> = {
  value: T;
  error: RequestError | null;
}

export default class BaseService {
  protected refs: Map<string, IServiceRefs<any>> = new Map();
  protected defaults: Map<string, IDefaultValues<any>> = new Map();

  public getRefs<T>(key: string, defaultValue: T, defaultError: RequestError | null = null): IServiceRefs<T> {
    if (this.refs.has(key)) {
      return this.refs.get(key)!;
    } else {
      this.defaults.set(key, {
        value: defaultValue,
        error: defaultError
      });
      const _ref: IServiceRefs<T> = {
        state: ref<RequestState>(RequestState.None),
        value: shallowRef<T>(defaultValue),
        error: ref(defaultError)
      };
      this.refs.set(key, _ref);
      return _ref;
    }
  }

  public updateRefState(key: string, value: RequestState) {
    if (this.refs.has(key)) {
      const _refs = this.refs.get(key)!;
      _refs.state.value = value;
    }

    return self;
  }

  public updateRefValue<T>(key: string, value: T) {
    if (this.refs.has(key)) {
      const _refs = this.refs.get(key)!;
      _refs.value.value = value;
    }

    return self;
  }

  public updateRefError(key: string, value: RequestError | null) {
    console.log("updateRefError [%s]: %s", key, JSON.stringify(value));
    if (this.refs.has(key)) {
      const _refs = this.refs.get(key)!;
      _refs.error.value = value;
    }

    return self;
  }

  public clearRefError(key: string) {
    if (this.refs.has(key)) {
      const _refs = this.refs.get(key)!;
      _refs.error.value = this.defaults.get(key)!.error;
    }

    return self;
  }

  public clearRefValue(key: string) {
    if (this.refs.has(key)) {
      const _refs = this.refs.get(key)!;
      _refs.value.value = this.defaults.get(key)!.value;
    }

    return self;
  }

  protected async refWrapper(refKey: string, fn: () => Promise<any>): Promise<void> {
    this.updateRefState(refKey, RequestState.Loading);
    try {
      const data = await fn();

      this.updateRefValue(refKey, data);
      this.updateRefState(refKey, RequestState.Ready);
    } catch (e) {
      this.updateRefError(refKey, e);
      this.updateRefState(refKey, RequestState.Failed);
    }
  }
}
